import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button, Typography } from '@mui/material';
import {
  selectBureauFormQuoteDetail,
  selectBureauFormTokenValidationStatus
} from 'src/store/selectors/bureauFormSelectors';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import theme from 'src/theme';
import Loading from 'src/components/Loading';
import DropdownMenu from 'src/components/DropdownMenu';
import { GET_QUOTE_INFORMATION } from 'src/store/actions/bureauFormActions';
import { loadAdminNumbers } from 'src/store/actions/parameterActions';
import clsx from 'clsx';
import validationSchema from './schema';
import makeInitialState from './initialState';
import useStyles from '../styles';

const StepZero = ({ data, handleNext }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const quoteInformation = useSelector(selectBureauFormQuoteDetail);
  const loadingQuote = useSelector(
    isLoadingSelector([GET_QUOTE_INFORMATION.action])
  );
  const tokenValidationStatus = useSelector(
    selectBureauFormTokenValidationStatus
  );

  const hasLoadedAdminNumbers = useRef(false);

  const initialState = useMemo(() => makeInitialState(data, quoteInformation), [
    data,
    quoteInformation
  ]);

  const onNext = useCallback(
    async values => {
      handleNext({
        ...values,
        term: values.term,
        rent: quoteInformation.availableTerms.find(
          term => term.key === values.term
        ).rent
      });
    },
    [handleNext, quoteInformation]
  );

  useEffect(() => {
    if (tokenValidationStatus === 'success' && !hasLoadedAdminNumbers.current) {
      hasLoadedAdminNumbers.current = true;
      dispatch(loadAdminNumbers());
    }
  }, [dispatch, tokenValidationStatus]);

  const availableTermsWithRent =
    quoteInformation?.availableTerms?.map(term => ({
      ...term,
      name: t('ENUMS.MONTH_RATE_WITH_RENT', {
        term: term.key,
        rent: term.rent.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      })
    })) || [];

  if (loadingQuote || !quoteInformation || tokenValidationStatus !== 'success')
    return (
      <Box
        sx={{
          height: {
            xs: 'auto',
            md: '50vh'
          },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Loading />
      </Box>
    );

  return (
    <Formik
      initialValues={initialState}
      validationSchema={validationSchema}
      onSubmit={onNext}
      enableReinitialize
    >
      {({
        errors,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        handleBlur
      }) => {
        return (
          <Box
            sx={{
              justifyContent: 'space-around',
              display: 'flex',
              flexDirection: 'column',
              height: {
                xs: 'auto',
                md: '50vh'
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                height: '50%'
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  marginBottom: '10px',
                  color: theme.palette.primary.main
                }}
              >
                {t('BUREAU_FORM.STEP_ZERO.TITLE')}
              </Typography>

              <DropdownMenu
                list={availableTermsWithRent}
                value={values.term}
                setValue={value => setFieldValue('term', value)}
                label={t('COMMON.TERM')}
                error={Boolean(touched.term && errors.term)}
                errorText={touched.term && t(errors.term)}
                onBlur={handleBlur}
              />
            </Box>

            <Box
              className={classes.box}
              display="flex"
              justifyContent="flex-end"
            >
              <Button
                className={clsx(
                  classes.buttons,
                  values.term && classes.buttonMain
                )}
                onClick={handleSubmit}
                color="primary"
                variant="outlined"
                disabled={!values.term}
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
};

StepZero.propTypes = {
  data: PropTypes.any.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default StepZero;
